<template>
  <b-modal
    ref="jobModal"
    id="jobDescriptionModal"
    size="lg"
    modal-class="job-description-modal"
    hide-footer
  >
    <!-- Header with Pagination -->
    <template #modal-header>
      <div class="w-100 d-flex justify-content-between align-items-center">
        <!-- <h5>{{ currentJob.title }}</h5> -->
        <span style="color: #006C6B" v-if="isInitialScout"
          >{{ currentIndex + 1 }}/{{ totalJobs }} 件表示</span
        >
        <span v-else style="width: 30px;"></span>
        <span
          @click="hide"
          style="cursor: pointer; margin-top: 5px; color: #006C6B; font-size: 24px;"
        >
          &times;
        </span>
      </div>
    </template>

    <!-- Job Description Content -->
     <div :class="{'jd-content': loading || errorMessage}">
        <Loader v-if="loading" />
        <div v-else-if="errorMessage" class="error-message description pad-1">
          {{ errorMessage }}
        </div>
        <div class="job-content" v-else>
          <div class="job-header pad-1" style="border-bottom: 1px solid #787878; height: 180px;">
            <h3 class="mb-2" style="font-weight: 600">{{ company }}</h3>
            <h5 style="height: 50px;" v-html="highlightText(currentJob.job_title, 'job_title')"></h5>
          </div>  
          <div v-html="highlightText(currentJob.job_description, 'job_description')" class="description pad-1"></div>
        </div>
     </div>

    <div class="modal-footer scout-footer w-100" v-if="!loading || !errorMessage">
      <div class="scout-button-section">
          <b-button
            @click="handleScout"
            :class="buttonClass"
            :disabled="isScouted"
          >
            {{buttonText}}
          </b-button>
          <p :class="isScouted ? 'scouted-hint' : 'scout-hint'">
            {{ scoutHintText }}
          </p>
        </div>
        <div class="scout-button-section">
          <b-button
            @click="handleNext"
            class="next-button"
            style="height: 44px;"
          >
            次を見る
          </b-button>
          <!-- <p class="scout-hint">企業からスカウトが届きやすくなります</p> -->
        </div>
    </div>
  </b-modal>
</template>

<script>
import Loader from "@/components/Loader.vue";
export default {
  name: "JobDescriptionModal",
  components: {
    Loader,
  },
  props: {
    errorMessage: {
      type: String,
      required: false,
    },
    currentJob: {
      type: Object,
      required: true,
    },
    currentIndex: {
      type: Number,
      required: true,
    },
    totalJobs: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    isJobScouted: {
      type: Boolean,
      required: false,
    },
    hasReachedScoutLimit: {
      type: Boolean, 
      required: false,
    },
    company: {
      type: String,
      required: false,
    },
    isInitialScout: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  computed: {
    isScouted() {
      return this.isJobScouted || this.hasReachedScoutLimit || false;
    },
    scoutHintText(){
      if(this.isJobScouted) {
        return '';
      } else if(this.hasReachedScoutLimit) {
        return 'スカウト希望は3件まで可能です';
      }
      return '企業からスカウトが届きやすくなります'
    },
    buttonText() {
      if(this.isJobScouted) {
        return 'スカウト希望済'
      } else {
        return 'スカウトを希望する'
      }
    },
    buttonClass() {
      if(this.isJobScouted) {
        return 'scouted-button'
      } else if(this.hasReachedScoutLimit) {
        return 'scout-limit-button'
      } else {
        return 'scout-button'
      }
    }
  },
  methods: {
    show() {
      this.$refs.jobModal.show();
    },
    hide() {
      this.$emit('close');
      this.$refs.jobModal.hide();
    },
    handleScout() {
      if (this.isScouted) {
        return;
      }
      this.$emit("scout");
    },
    handleNext() {
      this.$emit("next");
    },
    highlightText(text, field) {
      if (!text || !this.currentJob.highlights || !this.currentJob.highlights.length) return text;
      
      const fieldHighlights = this.currentJob.highlights.find(h => h.path === field);
      if (!fieldHighlights || !fieldHighlights.texts) return text;

      // Get hits and sort by length (longest first)
      const hits = fieldHighlights.texts
        .filter(text => text.type === 'hit')
        .map(text => text.value)
        .sort((a, b) => b.length - a.length);

      if (!hits.length) return text;

      // First handle full-width spaces between hits
      let highlightedText = this.highlightFullWidthSpaces(text, hits);
      // Then highlight the hits
      return this.highlightHits(highlightedText, hits);
    },

    highlightFullWidthSpaces(text, hits) {
      // Create patterns for all possible hit combinations with full-width space
      const patterns = hits.flatMap((hit1, index) =>
        hits.slice(index + 1).map((hit2) => ({
          pattern: new RegExp(
            `(${this.escapeRegExp(hit1)}　${this.escapeRegExp(hit2)})|(${this.escapeRegExp(hit2)}　${this.escapeRegExp(hit1)})`,
            'g'
          ),
          replacement: (_, g1, g2) => {
            const match = g1 || g2;
            const [first, second] = match.split('　');
            return `${first}<span class="highlight">　</span>${second}`;
          },
        }))
      );

      // Apply all patterns at once
      return patterns.reduce(
        (text, { pattern, replacement }) => text.replace(pattern, replacement),
        text
      );
    },

    highlightHits(text, hits) {
      const highlightedPositions = new Set();

      return hits.reduce((text, hit) => {
        const pattern = new RegExp(this.escapeRegExp(hit), 'g');
        return text.replace(pattern, (match, offset) => {
          const positions = Array.from(
            { length: match.length },
            (_, i) => offset + i
          );

          if (positions.every((pos) => !highlightedPositions.has(pos))) {
            positions.forEach((pos) => highlightedPositions.add(pos));
            return `<span class="highlight">${match}</span>`;
          }
          return match;
        });
      }, text);
    },

    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  },
};
</script>
<style scoped>
.scout-button-section {
  height: 90px;
  /* padding-top: 0px; */
}

.scout-footer {
  display: flex;
  justify-content: space-between;
  padding: 10px 0px 0px 0px;
}
.scout-hint {
  text-align: center;
  color: #ff0000;
  font-size: 9px;
  margin-top: 0px;
}
.scouted-hint {
  text-emphasis: center;
  color: #787878;
  font-size: 9px;
  margin-right: 10px;
  margin-top: 0px;
}
.placeholder-height {
  height: 10px;
}
.pad-1 {
  padding: 1rem;
}
.description {
  height: 60vh; 
  overflow-y: auto;
}
.jd-content {
  height: 60vh; /* Match the height of the job content */
  display: flex;
  flex-direction: column;
}

.error-message {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.error-content {
  text-align: center;
  color: #ff0000;
  font-size: 14px;
  line-height: 1.5;
}

>>> .highlight {
  background-color: #ffd700;
  padding: 0 2px;
  border-radius: 2px;
}

>>> .job-description-modal .modal-body {
  padding: 0px !important;
}
>>> .job-description-modal .modal-footer {
  background: #f2f5f7 !important;
  padding-bottom: 0px;
}

@media (min-width: 1024px) {
  .scout-footer {
    display: flex;
    justify-content: center;
    gap: 50px;
  }
}
</style>
